import './initZbarWasm';
import { createRoot } from 'react-dom/client';
import i18n from './shared/i18n/i18n';
import { registerAdminLocaleHotReload } from './shared/i18n/i18n.viteHot';
registerAdminLocaleHotReload(i18n);
import { initSentry } from 'pi-kiosk-shared/sentry';
import { appLogger } from './shared/logging/appLogger';
import { getRuntimeConfig, loadRuntimeConfig } from './runtimeConfig';
import { BrowserRouter } from 'react-router-dom';
import { QueryProvider } from './shared/providers/QueryProvider';
import { AdminSWRProvider } from './shared/swr/AdminSWRProvider.js';
import { AdminPwaColdStart } from './app/AdminPwaColdStart.js';
import { AdminChunkRecoveryOverlay } from './app/pwa/AdminChunkRecoveryOverlay.js';
import { AdminPwaLifecycle } from './app/pwa/AdminPwaLifecycle.js';
import {
  registerAdminChunkLoadRecovery,
} from './app/pwa/registerAdminChunkLoadRecovery.js';
import './shared/styles/tailwind.css';
import { applyInitialTheme } from './shared/styles/theme';
import { ThemeProvider } from './shared/styles/ThemeProvider.js';

registerAdminChunkLoadRecovery();
applyInitialTheme();

const rootElement = document.getElementById('root');

/**
 * PWA manifest shortcuts use `?shortcut=products|orders|…` (see vite.config).
 * Only capture/strip that param — never bare `?section=`, which analytics deep
 * links need (`section`+`group`+`tab`). Stripping `section` left tab in the URL
 * while Overview stayed selected (panel mounted with HTML `hidden`).
 */
function captureAdminPwaPendingShortcut(): void {
  const params = new URLSearchParams(window.location.search);
  const shortcut = params.get('shortcut');
  if (shortcut === null || shortcut.length === 0) {
    return;
  }
  try {
    sessionStorage.setItem('admin_pwa_pending_shortcut', shortcut);
  } catch {
    // ignore storage failures — still strip query params
  }
  params.delete('shortcut');
  const nextSearch = params.toString();
  const nextUrl =
    window.location.pathname +
    (nextSearch.length > 0 ? `?${nextSearch}` : '') +
    window.location.hash;
  window.history.replaceState(null, '', nextUrl);
}

async function bootstrap(): Promise<void> {
  await loadRuntimeConfig();

  const runtimeConfig = getRuntimeConfig();
  try {
    initSentry({
      dsn: runtimeConfig?.sentryDsn ?? import.meta.env.VITE_SENTRY_DSN,
      environment:
        runtimeConfig?.sentryEnvironment ?? import.meta.env.VITE_SENTRY_ENVIRONMENT,
      release: runtimeConfig?.buildVersion ?? import.meta.env.VITE_BUILD_VERSION,
      app: 'admin',
      isProd: import.meta.env.PROD,
    });
  } catch (err) {
    appLogger.warn('Sentry init failed', { error: err });
  }

  if (!rootElement) {
    throw new Error('Root element not found');
  }

  captureAdminPwaPendingShortcut();

  const App = (await import('./App')).default;

  createRoot(rootElement).render(
    <ThemeProvider>
      <BrowserRouter>
        <QueryProvider>
          <AdminSWRProvider>
            <AdminPwaColdStart />
            <App />
            {/* After App so equal-z paint order cannot bury PWA chrome under lists. */}
            <AdminPwaLifecycle />
            <AdminChunkRecoveryOverlay />
          </AdminSWRProvider>
        </QueryProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

void bootstrap();
